import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { APP_CONFIG } from '@shared/config/app.config';
import { ICollection, IDtoResponse } from '@app/shared/models/dtos/response.dto';
import {
  IKycInfo,
  IOnboarding,
  IOnboardingPjResponse,
  IOnboardingPjProspectInfo,
  IOnboardingRequirement,
  IResetOcrRequest,
  ISwitchUserRequest,
  ISwitchUserResponse,
  IKycRequirement,
} from '@shared/models/onboarding.model';
import { CollectionParam, SearchParam } from '@app/shared/models/param.model';
import { AmplitudeService } from '../amplitude.service';
import { AppContext } from '../context/app.context.service';
import { Title } from '@angular/platform-browser';
import { ICustomerWrapper } from '@app/shared/models/customer.model';
import { ICustomerContext } from '@app/shared/models/context/customer.context.model';
import { IMarks, IRefinanceAvailability, IRanking, ISearch } from '@app/shared/models/user.model';
import { CustomerContext } from '../context/customer.context.service';
import { AccountContext } from '../context/account.context.service';
import { ContractContext } from '../context/contract.context.service';
import { ServiceBase } from '../base/base.service';
import { UserContext } from '../context/user.context.service';
import { AMPLITUDE_EVENT, SEARCH_EVENT_PROPERTY } from '@app/shared/types/enums/amplitude';
import { USER_TYPE } from '@app/shared/types/enums';
import { Utility } from '@app/shared/utils/utility';
import { PATH } from '@app/shared/types/enums/path';
import { ActionEnum } from '@app/shared/types/enums/ActionEnum.enum';
import { channel } from 'version';
import { IAsyncAction } from '@app/shared/models/async-event';

@Injectable({
  providedIn: 'root',
})
export class UserService extends ServiceBase {
  constructor(
    private readonly _http: HttpClient,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _accountCtxSvc: AccountContext,
    private readonly _contractCtxSvc: ContractContext,
    private readonly _appCtxSvc: AppContext,
    private readonly _userCtxSvc: UserContext,
    private readonly _titleSvc: Title
  ) {
    super(`${APP_CONFIG.serviceBaseUrl}/onboarding/api/v1/user`);
  }

  searchCustomer(searchParam: SearchParam): Observable<ISearch | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.HOME).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<ISearch>>(`${this.baseUrl}/search`, {
        params: new HttpParams().set('value', searchParam.value).set('by', searchParam.by),
        headers,
      })
      .pipe(
        tap(res =>
          this._amplitudeSvc.logEvent<SEARCH_EVENT_PROPERTY>(AMPLITUDE_EVENT.SEARCH, {
            rppUserId: res.data?.rppUserId,
            by: searchParam.by,
            byValue: searchParam.value,
          })
        ),
        map(response => response.data)
      );
  }

  loadCustomerContext(id: string): Observable<boolean> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.HOME).set('monitoring_action', ActionEnum.READ);

    this._accountCtxSvc.clear();
    this._contractCtxSvc.clear();

    if (this._appCtxSvc.getUserType() === USER_TYPE.PJ) {
      this._customerCtxSvc.setContext(
        Utility.convertBusinessToCustomerContext(this._appCtxSvc.getBusinessInfo(), this._appCtxSvc.getBusinessUserInfo())
      );

      return of(true);
    } else {
      return this._http
        .get<IDtoResponse<ICustomerContext>>(`${this.baseUrl}/${id}/context`, {
          headers,
        })
        .pipe(
          tap((response: IDtoResponse<ICustomerContext>) => {
            if (response.success && response.data?.rppUserId) {
              this._amplitudeSvc.setCustomerProperties({
                rppUserId: response.data?.rppUserId,
                country: response.data?.country,
              });
              this._appCtxSvc.setUseAppContextCountry(false);
              this._userCtxSvc.setWentThroughAccessLevelCheck(false);
              this._customerCtxSvc.setContext(response.data);
              this._titleSvc.setTitle(`Care | ${response.data?.country} ${response.data?.userId}`);
            }
          }),
          map(res => res.success)
        );
    }
  }

  getProfile(id: string): Observable<ICustomerWrapper | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.HOME).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<ICustomerWrapper>>(`${this.baseUrl}/${id}/profile`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  getWaitingListRanking(rppUserId: string): Observable<IRanking | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.HOME).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<IRanking>>(`${this.baseUrl}/${rppUserId}/signup/waiting-line`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  getMarks(rppUserId: string): Observable<IMarks | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.HOME).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<IMarks>>(`${this.baseUrl}/${rppUserId}/marks`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  getRefinanceAvailability(rpp_user_id: string): Observable<IRefinanceAvailability | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.HOME).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<IRefinanceAvailability>>(`${APP_CONFIG.serviceBaseUrl}/wallet/api/v1/refinancing/available/${rpp_user_id}`, {
        headers,
      })
      .pipe(map(result => result.data));
  }

  /**
   *
   * @deprecated Use getKyc instead
   *
   */
  getKycInfo(rppUserId: string): Observable<IDtoResponse<IKycInfo>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.READ);

    return this._http.get<IDtoResponse<IKycInfo>>(`${this.baseUrl}/${rppUserId}/kyc/info`, {
      headers,
    });
  }

  getKyc(rppUserId: string): Observable<IKycInfo | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<IKycInfo>>(`${this.baseUrl}/${rppUserId}/kyc/info`, {
        headers,
      })
      .pipe(map(response => response.data));
  }

  /**
   * @deprecated use getUserOnboardings
   * FIXME This service must have ICollection Wrapper. No matter if not have pagination.    *
   */
  getOnboardings(rppUserId: string): Observable<IDtoResponse<IOnboarding[]>> {
    return this._http.get<IDtoResponse<IOnboarding[]>>(`${this.baseUrl}/${rppUserId}/signup/products`);
  }

  getUserOnboardings(rppUserId: string): Observable<IOnboarding[] | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.READ);

    if (!rppUserId || rppUserId.trim() === '') {
      return of(undefined);
    }

    return this._http
      .get<IDtoResponse<IOnboarding[]>>(`${this.baseUrl}/${rppUserId}/signup/products`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  /**
   * FIXME This service must have ICollection Wrapper. No matter if not have pagination.    *
   */
  getOnboardingRequirements(rppUserId: string, onboardingId: number): Observable<IOnboardingRequirement[] | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<IOnboardingRequirement[]>>(`${this.baseUrl}/${rppUserId}/signup/product/${onboardingId}`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  resetOcr(data: IResetOcrRequest): Observable<IDtoResponse<number>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.CREATE);

    return this._http.post<IDtoResponse<number>>(`${this.baseUrl}/action/reset-ocr`, data, {
      headers,
    });
  }

  updateRequirementStatus(data: IKycRequirement): Observable<IDtoResponse<number>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.UPDATE);

    return this._http.put<IDtoResponse<number>>(`${this.baseUrl}/action/onboarding-requirement-manual-review`, data, {
      headers,
    });
  }

  switchUser(rppUserIdOrigin: string, rppUserIdDestination: string): Observable<IDtoResponse<ISwitchUserResponse>> {
    const data: ISwitchUserRequest = {
      rppUserIdDestination,
    };
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.READ);

    return this._http.put<IDtoResponse<ISwitchUserResponse>>(`${this.baseUrl}/${rppUserIdOrigin}/action/switch`, data, {
      headers,
    });
  }

  activateFaceAuthValidation(rppUserId: string): Observable<IDtoResponse<void>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.UPDATE);

    return this._http.put<IDtoResponse<void>>(`${this.baseUrl}/${rppUserId}/action/activate-face-auth-validation`, null, {
      headers,
    });
  }

  getOnboardingPj(rppBusinessId: string): Observable<IOnboardingPjResponse | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<IOnboardingPjResponse>>(`${APP_CONFIG.serviceBaseUrl}/onboarding/api/v1/business/${rppBusinessId}/kyb/info`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  getOnboardingPjProspect(prospectId: string): Observable<IOnboardingPjProspectInfo | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<IOnboardingPjProspectInfo>>(
        `${APP_CONFIG.serviceBaseUrl}/onboarding/api/v1/business/${prospectId}/prospects/info`,
        {
          headers,
        }
      )
      .pipe(map(res => res.data));
  }

  getUserActions(rppUserId: string, params: CollectionParam): Observable<ICollection<IAsyncAction> | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ONBOARDING).set('monitoring_action', ActionEnum.READ);

    return this._http
      .get<IDtoResponse<ICollection<IAsyncAction>>>(`${APP_CONFIG.serviceBaseUrl}/onboarding/api/v1/user-actions/${rppUserId}`, {
        params: new HttpParams().set('pageIndex', params.pageIndex.toString()).set('pageSize', params.pageSize.toString()),
        headers,
      })
      .pipe(map(response => (response.data as any)?.data));
  }
}
